import { useCallback, useState } from "react";
import { Trash2 } from "lucide-react";

import { AiAssistButton, AiDraftNotice, useAiAssist } from "@/components/communications/AiAssist";
import { MESSAGE_BODY_MAX } from "@/components/communications/threadFormat";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/Textarea";

/**
 * Reply, under the thread, the way every mail client puts it: the address it
 * goes to, the message, Send and Discard. The subject is the thread's own and
 * is not offered for editing — rewriting it mid-thread is how a thread stops
 * looking like one. The AI answers against this thread (`onDraft`), writing
 * from the recruiter's note or improving what they typed.
 *
 * In flight from the click until the response lands: two clicks on Send are
 * two emails to a real person.
 */
const ReplyBox = ({ to, onSend, onDraft, onDiscard }) => {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const apply = useCallback((next) => setBody(next.body || ""), []);
  const assist = useAiAssist({ value: { subject: "", body }, onChange: apply, request: onDraft });
  const busy = sending || assist.running;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy || !body.trim()) return;
    setSending(true);
    try {
      const sent = await onSend(body.trim());
      if (sent) {
        setBody("");
        assist.dismiss();
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <form aria-label="Reply" onSubmit={handleSubmit} className="rounded-lg border border-border-primary bg-background-secondary shadow-sm">
      <p className="border-b border-border-primary px-4 py-2 text-xs text-text-secondary">
        To <span className="text-text-primary">{to}</span>
      </p>
      <label htmlFor="reply-body" className="sr-only">
        Message
      </label>
      <Textarea
        id="reply-body"
        autoFocus
        value={body}
        maxLength={MESSAGE_BODY_MAX}
        rows={6}
        disabled={assist.running}
        onChange={(e) => setBody(e.target.value)}
        className="resize-y rounded-none border-0 bg-transparent focus-visible:ring-0"
        placeholder={assist.running ? "Writing…" : "Write your reply, or ask the AI to write it"}
      />
      <AiDraftNotice assist={assist} className="mx-3 mb-3" />
      <div className="flex items-center justify-between gap-2 px-3 pb-3">
        <div className="flex items-center gap-2">
          <Button type="submit" variant="secondary" disabled={busy || !body.trim()}>
            {sending ? "Sending…" : "Send"}
          </Button>
          <AiAssistButton assist={assist} disabled={sending} />
        </div>
        <Button type="button" variant="ghost" size="icon-sm" className="rounded-lg" aria-label="Discard reply" disabled={busy} onClick={onDiscard}>
          <Trash2 aria-hidden="true" />
        </Button>
      </div>
    </form>
  );
};

export default ReplyBox;
